<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { staticPageHref, accountHref, type AccountSection } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import AppLogo from '../ui/AppLogo.vue'

/** Élément de nav actif (clé sous auth.account.nav). La home = 'dashboard'. */
withDefaults(defineProps<{ active?: string }>(), { active: 'dashboard' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member, logout } = useAuth()
const { hasFsmeCard } = useContracts()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))

/** Lien d'aide externe du client, localisé (FR / EN). */
const HELP_HREF: Record<Locale, string> = {
  fr: 'https://smerra.fr/contacts/',
  en: 'https://smerra.fr/en/contact-us/',
}

/** Entrées de la sidebar. `key` → libellé i18n ; `soon` = sous-page à venir
 *  (placeholder non navigable) ; `href` = lien externe (aide/contact). */
/** Nav interne groupée par section. `label` = clé i18n du titre (null = pas de
 *  titre, ex. l'accueil tout en haut) ; `items` = clés de section/nav.
 *  Sans contrat actif remonté à FSME, l'adhérent n'a pas de carte de tiers
 *  payant : l'entrée disparaît de la nav (page également inaccessible, cf.
 *  CardView). */
const navSections = computed(() => [
  { label: null as string | null, items: ['dashboard'] },
  {
    label: 'coverage',
    items: hasFsmeCard.value
      ? ['contracts', 'card', 'documents', 'reimbursements', 'subscribe']
      : ['contracts', 'documents', 'reimbursements', 'subscribe'],
  },
  {
    label: 'account',
    // Le RIB de remboursement (« payment ») n'existe que pour un adhérent
    // éligible FSME : sans éligibilité, l'entrée disparaît (page aussi
    // inaccessible, cf. PaymentView).
    items: hasFsmeCard.value
      ? ['info', 'credentials', 'payment']
      : ['info', 'credentials'],
  },
])
const helpItems = computed(() => [
  { key: 'help', external: true, href: HELP_HREF[currentLocale.value] },
  { key: 'contact', external: false, href: '' },
])

/** URL localisée d'une entrée de nav (section ou 'dashboard'). */
function navHref(key: string): string {
  return accountHref(key as AccountSection | 'dashboard', currentLocale.value)
}

/** Menu mobile (hamburger) : replié par défaut, se referme à chaque navigation. */
const navOpen = ref(false)
watch(() => route.fullPath, () => { navOpen.value = false })

// Menu déroulant « Nom ▾ »
const menuOpen = ref(false)
const memberEl = ref<HTMLElement | null>(null)
function toggleMenu() { menuOpen.value = !menuOpen.value }
function onDocClick(e: MouseEvent) {
  if (menuOpen.value && memberEl.value && !memberEl.value.contains(e.target as Node)) {
    menuOpen.value = false
  }
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') menuOpen.value = false
}
onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
})

function onLogout() {
  menuOpen.value = false
  logout()
  router.push(loginHref.value)
}
</script>

<template>
  <div class="acc">
    <!-- Header connecté : logo + salutation + menu membre -->
    <header class="acc__bar">
      <AppLogo />
      <p v-if="member" class="acc__greeting">
        {{ t('auth.account.greeting', { name: member.firstName }) }}
      </p>

      <div v-if="member" ref="memberEl" class="acc__member">
        <button
          type="button"
          class="acc__member-btn"
          :aria-expanded="menuOpen"
          aria-haspopup="menu"
          @click="toggleMenu"
        >
          <span class="acc__member-id">
            <span class="acc__member-name">{{ member.firstName }} {{ member.lastName }}</span>
            <span class="acc__member-num">{{ t('auth.account.memberNumber') }} : {{ member.memberNumber }}</span>
          </span>
          <svg class="acc__chevron" :class="{ 'is-open': menuOpen }" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <ul v-if="menuOpen" class="acc__member-menu" role="menu">
          <li role="none">
            <button type="button" role="menuitem" @click="onLogout">
              {{ t('auth.account.menu.logout') }}
            </button>
          </li>
        </ul>
      </div>
    </header>

    <div class="acc__body">
      <!-- Sidebar -->
      <aside class="acc__sidebar" aria-label="Navigation espace personnel">
        <button
          type="button"
          class="acc__nav-toggle"
          :aria-expanded="navOpen"
          aria-controls="acc-nav-panel"
          @click="navOpen = !navOpen"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path v-if="navOpen" d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <path v-else d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
          <span>{{ navOpen ? t('auth.account.nav.close') : t('auth.account.nav.menu') }}</span>
        </button>

        <div id="acc-nav-panel" class="acc__nav-panel" :class="{ 'is-open': navOpen }">
        <nav class="acc__nav" aria-label="Rubriques">
          <div
            v-for="(section, i) in navSections"
            :key="i"
            class="acc__nav-section"
            :class="{ 'acc__nav-section--titled': section.label }"
          >
            <p v-if="section.label" class="acc__nav-heading">
              <svg class="acc__nav-heading-icon" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                <path
                  v-if="section.label === 'coverage'"
                  d="M12 3.5l6.5 2.5v5c0 4-2.7 6.9-6.5 8.5-3.8-1.6-6.5-4.5-6.5-8.5V6L12 3.5z"
                  fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"
                />
                <template v-else>
                  <circle cx="12" cy="8.5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M5.5 19c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </template>
              </svg>
              {{ t(`auth.account.nav.sections.${section.label}`) }}
            </p>
            <ul>
              <li v-for="item in section.items" :key="item">
                <router-link
                  :to="navHref(item)"
                  class="acc__nav-link"
                  :class="{ 'acc__nav-link--active': item === active }"
                  :aria-current="item === active ? 'page' : undefined"
                >
                  <span>{{ t(`auth.account.nav.${item}`) }}</span>
                  <svg class="acc__nav-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                    <path d="M8 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </router-link>
              </li>
            </ul>
          </div>
        </nav>

        <nav class="acc__nav acc__nav--secondary" aria-label="Aide">
          <p class="acc__nav-heading">
            <svg class="acc__nav-heading-icon" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
              <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
              <path d="M9.7 9.2a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <circle cx="12" cy="16.3" r="0.6" fill="currentColor"/>
            </svg>
            {{ t('auth.account.nav.sections.help') }}
          </p>
          <ul>
            <li v-for="item in helpItems" :key="item.key">
              <a v-if="item.external" :href="item.href" target="_blank" rel="noopener" class="acc__nav-link">
                <span>{{ t(`auth.account.nav.${item.key}`) }}</span>
                <svg class="acc__nav-extlink" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                  <path d="M8 5H5v10h10v-3M11 4h5v5M16 4l-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <router-link
                v-else
                :to="navHref(item.key)"
                class="acc__nav-link"
                :class="{ 'acc__nav-link--active': item.key === active }"
                :aria-current="item.key === active ? 'page' : undefined"
              >
                <span>{{ t(`auth.account.nav.${item.key}`) }}</span>
                <svg class="acc__nav-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                  <path d="M8 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </router-link>
            </li>
          </ul>
        </nav>

        <div class="acc__nav-foot">
          <button type="button" class="acc__nav-link acc__logout" @click="onLogout">
            {{ t('auth.account.nav.logout') }}
          </button>
        </div>
        </div>
      </aside>

      <!-- Contenu -->
      <main id="main" class="acc__main">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.acc {
  --acc-sidebar-w: 15rem;
  background: linear-gradient(180deg,
    color-mix(in oklab, var(--color-peach) 22%, white) 0%,
    color-mix(in oklab, var(--color-pink) 12%, white) 100%);
  min-height: 70vh;
}

/* Barre membre */
.acc__bar {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  max-width: 82rem;
  margin-inline: auto;
  padding: 1.25rem var(--layout-gutter);
}
.acc__greeting {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-chocolate);
}
.acc__member {
  position: relative;
  margin-left: auto;
}
.acc__member-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--color-chocolate);
}
.acc__member-id {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}
.acc__member-name { font-size: var(--text-sm); font-weight: 700; }
.acc__member-num { font-size: var(--text-xs); color: var(--text-muted); white-space: nowrap; }
.acc__chevron { color: var(--text-muted); transition: transform var(--dur-fast) var(--ease-out); }
.acc__chevron.is-open { transform: rotate(180deg); }
.acc__member-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  min-width: 11rem;
  padding: 0.375rem;
  background: var(--color-white);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  z-index: var(--z-header);
}
.acc__member-menu button {
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.625rem;
  border: 0;
  background: none;
  border-radius: var(--radius-xs);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
}
.acc__member-menu button:hover { background: var(--bg-soft); color: var(--color-cherry); }

/* Corps : sidebar + contenu */
.acc__body {
  display: grid;
  grid-template-columns: var(--acc-sidebar-w) 1fr;
  gap: 1.5rem;
  max-width: 82rem;
  margin-inline: auto;
  padding: 0 var(--layout-gutter) clamp(2rem, 5vw, 3.5rem);
  align-items: start;
}
.acc__sidebar {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
/* Panneau de nav : toujours déplié sur desktop ; replié sous le hamburger
   sur mobile. */
.acc__nav-panel { display: flex; flex-direction: column; gap: 1.25rem; }
/* Bouton hamburger : masqué sur desktop, affiché sur mobile. */
.acc__nav-toggle {
  display: none;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.6rem 0.9rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--color-white);
  color: var(--color-chocolate);
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
}
.acc__nav ul {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-left: 0.35rem;
  padding-left: 0.5rem;
  border-left: 2px solid transparent;
}
.acc__nav-section + .acc__nav-section { margin-top: 1rem; }
/* Rail vertical le long des liens (sous le titre). Transparent pour l'accueil
   (sans titre) afin de garder tous les libellés alignés. */
.acc__nav-section--titled ul,
.acc__nav--secondary ul { border-left-color: color-mix(in oklab, var(--primary) 25%, var(--border)); }
/* Titre de section : aligné à gauche (hors rail), fond blanc « façon section »,
   plus lisible (taille + contraste). */
.acc__nav-heading {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: color-mix(in oklab, var(--color-chocolate) 60%, var(--text-muted));
  background: color-mix(in oklab, var(--color-white) 70%, transparent);
  border-radius: var(--radius-sm);
}
/* Picto thématique : accent orange pour différencier les titres des liens. */
.acc__nav-heading-icon { flex-shrink: 0; color: var(--primary); }
/* Accueil (section sans titre) : item de 1er niveau, aligné à gauche avec les
   titres, sans rail. */
.acc__nav-section:not(.acc__nav-section--titled) ul {
  margin-left: 0;
  padding-left: 0;
  border-left: 0;
}
.acc__nav-section:not(.acc__nav-section--titled) .acc__nav-link { justify-content: center; text-align: center; }
.acc__nav-section:not(.acc__nav-section--titled) .acc__nav-chevron { display: none; }
/* Accueil inactif (sur une sous-page) : bouton « retour », fond blanc, orange,
   bordure orange et flèche devant. Au survol, il se remplit d'orange. */
.acc__nav-section:not(.acc__nav-section--titled) .acc__nav-link:not(.acc__nav-link--active) {
  background: var(--color-white);
  color: var(--primary);
  border: 1px solid var(--primary);
}
.acc__nav-section:not(.acc__nav-section--titled) .acc__nav-link:not(.acc__nav-link--active)::before {
  content: '←';
  font-weight: 700;
  line-height: 1;
}
.acc__nav-section:not(.acc__nav-section--titled) .acc__nav-link:not(.acc__nav-link--active):hover {
  background: var(--primary);
  color: var(--text-on-primary);
}
.acc__nav-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  padding: 0.4rem 0.7rem;
  border: 0;
  background: none;
  border-radius: var(--radius-pill);
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
}
/* Libellés longs (ex. « Mon RIB de remboursement ») : retour à la ligne plutôt
   que troncature ; on coupe au besoin les mots trop longs. */
.acc__nav-link span { min-width: 0; overflow-wrap: anywhere; }
.acc__nav-link:hover { background: color-mix(in oklab, var(--color-white) 70%, transparent); }
.acc__nav-link--active,
.acc__nav-link--active:hover {
  background: var(--primary);
  color: var(--text-on-primary);
}
/* Sous-pages à venir : curseur « main » au survol pour signaler le clic. */
.acc__nav-link--soon { cursor: pointer; }

/* Chevron « ça navigue » : discret au repos, marqué au survol. */
.acc__nav-chevron {
  flex-shrink: 0;
  color: var(--text-muted);
  opacity: 0.45;
  transition: opacity var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out);
}
.acc__nav-link:hover .acc__nav-chevron { opacity: 1; transform: translateX(2px); }
.acc__nav-link--active .acc__nav-chevron,
.acc__nav-link--active:hover .acc__nav-chevron { color: var(--text-on-primary); opacity: 0.9; }
.acc__nav-extlink { flex-shrink: 0; color: var(--text-muted); opacity: 0.7; }
.acc__nav-link:hover .acc__nav-extlink { opacity: 1; }

/* Pied de sidebar : séparateur + déconnexion en rouge. */
.acc__nav-foot { border-top: 1px solid var(--border); padding-top: 0.5rem; }
.acc__logout { color: var(--color-cherry); }
.acc__logout:hover { background: color-mix(in oklab, var(--color-cherry) 10%, white); }

.acc__main { min-width: 0; }

/* Responsive : la sidebar devient un menu hamburger replié au-dessus du contenu. */
@media (max-width: 64rem) {
  .acc__body { grid-template-columns: 1fr; }
  .acc__sidebar { position: static; }
  .acc__nav-toggle { display: flex; }
  .acc__nav-panel { display: none; }
  .acc__nav-panel.is-open { display: flex; }
}
@media (max-width: 48rem) {
  .acc__bar { flex-wrap: wrap; gap: 0.75rem; }
  .acc__greeting { order: 3; width: 100%; }
}
</style>
